/** Formatting helpers shared by the admin bill detail modal and the public
 * customer-facing bill page. */
import axios from "axios";
import type { TFunction } from "i18next";

/** Localised label for a bill lifecycle status (`draft`/`issued`/`paid`/…), the
 * one definition shared by the billing table, the bill detail modal and the
 * public customer-facing bill page. */
export function billStatusLabel(t: TFunction, status: string): string {
  const humanised = status.charAt(0).toUpperCase() + status.slice(1);
  return t(`billing.status.${status}`, { defaultValue: humanised });
}

/** Localised label for a billing PERIOD's status (`open`/`billed`/`closed`) — a
 * distinct vocabulary from a bill's own status, so it gets its own key rather
 * than sharing `billing.status.*`. */
export function billPeriodStatusLabel(t: TFunction, status: string): string {
  const humanised = status.charAt(0).toUpperCase() + status.slice(1);
  return t(`billing.periodStatus.${status}`, { defaultValue: humanised });
}

/** basename mirrors App.tsx: Vite's BASE_URL without the trailing slash. */
export function publicBillUrl(token: string): string {
  const base = import.meta.env.BASE_URL.replace(/\/$/, "");
  return `${window.location.origin}${base}/bill/${token}`;
}

/** Extract a single readable message from a FastAPI error response — `detail`
 * is a plain string for a raised `HTTPException` (e.g. the 409 "period isn't
 * open" guard) or a list of Pydantic error objects for a 422 validation
 * failure (e.g. the "period hasn't fully elapsed yet" rule). */
export function apiErrorMessage(err: unknown, fallback: string): string {
  if (axios.isAxiosError(err)) {
    const detail = err.response?.data?.detail;
    if (typeof detail === "string") return detail;
    if (Array.isArray(detail)) {
      const msgs = detail.map((d) => (typeof d?.msg === "string" ? d.msg : null)).filter(Boolean);
      if (msgs.length > 0) return msgs.join("; ");
    }
  }
  return fallback;
}
